import mapboxgl from 'mapbox-gl';

import {CSRF_COOKIE_NAME, DESIGN_MAP_STYLE_URL, MAPBOX_ACCESS_TOKEN, DEFAULT_MAP_STYLE_URL} from '../../constants';
import {ElObj} from '../../elobj';
import {getLogger} from '../../logging';
import {svc} from '../../request';
import {attachAccountMenu} from '../../ui/accountmenu';
import {dealBlockFromSummary, renderDealBlock} from '../../ui/dealblock';
import {clampFixedMenuToViewport, flipPopoverHorizontally} from '../../ui/popover';
import {extractCookie} from '../../util';
import {
	animateProjectFly,
	consumeProjectFlyBack,
	projectSurfaceSelector,
	rectFromElement,
	setProjectSurfaceData,
} from '../projecttransition';

const logger = getLogger('projectdashboardview');
const ONBOARDING_STORAGE_KEY = 'lb:dashboard:onboarding-dismissed';

type DashboardViewMode = 'grid' | 'shelf' | 'map' | 'list';
type DashboardSortMode = 'recent' | 'name' | 'size' | 'status';
type DashboardStatus = 'draft' | 'checkout' | 'ready';

interface IDashboardProject {
	latitude: number | null;
	longitude: number | null;
	mapImageUrl: string;
	project: IProject;
	quantity: number | null;
	status: DashboardStatus;
	zoom: number | null;
}

const STATUS_LABELS: {[status in DashboardStatus]: string} = {
	checkout: 'Checkout',
	draft: 'Draft',
	ready: 'Ready',
};

const STATUS_ORDER: {[status in DashboardStatus]: number} = {
	draft: 0,
	checkout: 1,
	ready: 2,
};

const STATUS_DOTS: {[status in DashboardStatus]: string} = {
	checkout: '#C8941F',
	draft: '#94A3B8',
	ready: '#5DC799',
};

function createEl<K extends keyof HTMLElementTagNameMap>(
	tagName: K,
	className?: string,
	text?: string,
): HTMLElementTagNameMap[K] {
	const el = document.createElement(tagName);
	if (className) {
		el.className = className;
	}
	if (text !== undefined) {
		el.textContent = text;
	}
	return el;
}

function displayTitle(project: IProject): string {
	const title = project.title.trim();
	return title.length > 0 ? title : 'Untitled Project';
}

function formatNumber(value: number | null): string {
	return value === null ? '...' : value.toLocaleString();
}

function statusForProject(project: IProject): DashboardStatus {
	if (project.absoluteListUrl) {
		return 'ready';
	}
	if (project.paymentAllowed && project.paymentRequired) {
		return 'checkout';
	}
	return 'draft';
}

function projectPlaceLabel(project: IProject): string {
	// Was `${slug} saved selection` — the suffix was static across every
	// card and added noise without information. Drop to just the slug.
	return project.slug;
}

function projectSortName(project: IProject): string {
	return displayTitle(project).toLocaleLowerCase();
}

function syntheticPointForProject(project: IProject): {left: number; top: number} {
	let hash = 0;
	for (const ch of project.slug) {
		hash = ((hash << 5) - hash) + ch.charCodeAt(0);
		hash |= 0;
	}
	const abs = Math.abs(hash);
	return {
		left: 12 + (abs % 74),
		top: 18 + ((Math.floor(abs / 97)) % 58),
	};
}

export class ProjectDashboardView {
	private accountMenuTemplate: string | null;
	private actionMenuEl: HTMLElement | null;
	private dashEl: HTMLElement | null;
	private documentClickHandler: (event: MouseEvent) => void;
	private documentKeyDownHandler: (event: KeyboardEvent) => void;
	private filterStatus: DashboardStatus | 'all';
	private map: mapboxgl.Map | null;
	private mapCanvasEl: HTMLElement | null;
	private mapPinsEl: HTMLElement | null;
	private maybePlayedFlyBack: boolean;
	private navPending: boolean;
	private onboardingVisible: boolean;
	// Deal-forward PR C: per-user dashboard mode. Hydrated from /api/ui in
	// init(); 'lists' leads with projects (today's layout), 'deals' leads
	// with the deal grid and demotes projects below.
	private mode: DashboardMode;
	private deals: Array<IDealSummary>;
	private projects: Array<IDashboardProject>;
	private query: string;
	private renderQueued: number | null;
	private root: HTMLElement;
	private searchInputEl: HTMLInputElement | null;
	private sortMode: DashboardSortMode;
	private viewMode: DashboardViewMode;

	constructor(rootEl: ElObj) {
		const root = rootEl.element();
		if (!(root instanceof HTMLElement)) {
			throw new Error('ProjectDashboardView: root element is missing');
		}
		this.accountMenuTemplate = null;
		this.actionMenuEl = null;
		this.dashEl = null;
		this.documentClickHandler = this.documentClick.bind(this);
		this.documentKeyDownHandler = this.documentKeyDown.bind(this);
		this.filterStatus = 'all';
		this.map = null;
		this.mapCanvasEl = null;
		this.mapPinsEl = null;
		this.maybePlayedFlyBack = false;
		this.navPending = false;
		this.onboardingVisible = window.localStorage.getItem(ONBOARDING_STORAGE_KEY) !== '1';
		this.mode = 'lists';
		this.deals = [];
		this.projects = [];
		this.query = '';
		this.renderQueued = null;
		this.root = root;
		this.searchInputEl = null;
		this.sortMode = 'recent';
		this.viewMode = 'grid';
		document.body.classList.add('lb-redesign-projects-body');
		document.addEventListener('click', this.documentClickHandler);
		document.addEventListener('keydown', this.documentKeyDownHandler);
		this.init();
	}

	// ⌘K / Ctrl+K focuses the search input. Matches the shortcut chip
	// shown in `.lb-dash-search` and the existing Project Detail
	// `documentKeyDownEvent` pattern. Ignore the shortcut while typing
	// in a non-search input/textarea so it doesn't hijack form keys.
	private documentKeyDown(event: KeyboardEvent): void {
		if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') {
			return;
		}
		event.preventDefault();
		if (this.searchInputEl) {
			this.searchInputEl.focus();
			this.searchInputEl.select();
		}
	}

	private async archiveProject(dashProject: IDashboardProject): Promise<void> {
		await svc.project.archive(dashProject.project.slug);
		this.projects = this.projects.filter(obj => obj.project.slug !== dashProject.project.slug);
		this.render();
	}

	private avatarText(project: IProject): string {
		const title = displayTitle(project).replace(/[^A-Za-z0-9 ]/g, '').trim();
		const parts = title.split(/\s+/).filter(Boolean);
		if (parts.length > 1) {
			return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
		}
		return project.slug.slice(0, 2).toUpperCase();
	}

	private bodyClasses(): string {
		return [
			'lb-dash',
			'lb-redesign-surface',
			this.viewMode === 'map' ? 'lb-dash--map' : '',
		].filter(Boolean).join(' ');
	}

	private buildActionsButton(dashProject: IDashboardProject): HTMLElement {
		const btn = createEl('button', 'lb-dash-icon-btn lb-dash-card__menu');
		btn.type = 'button';
		btn.title = 'Project actions';
		btn.setAttribute('aria-label', 'Project actions');
		btn.appendChild(createEl('span', 'material-icons', 'more_horiz'));
		btn.addEventListener('click', event => {
			event.preventDefault();
			event.stopPropagation();
			this.openActionMenu(dashProject, btn);
		});
		return btn;
	}

	private buildAvatar(project: IProject): HTMLElement {
		return createEl('span', 'lb-dash-avatar', this.avatarText(project));
	}

	// Returns a fresh account-menu element each render. _account_menu.html
	// (rendered into base.html's hidden top bar) is the single source of
	// truth; we cache its outerHTML on first build and clone-then-rewire
	// the popover on subsequent renders. Without cloning, the original
	// element would be lost on the first dashEl.remove() in render().
	private buildAccountMenu(): HTMLElement | null {
		if (this.accountMenuTemplate === null) {
			const fromBar = document.querySelector<HTMLElement>('[data-lb-account-menu]');
			if (!fromBar) {
				return null;
			}
			this.accountMenuTemplate = fromBar.outerHTML;
			fromBar.remove();
		}
		const wrap = document.createElement('div');
		wrap.innerHTML = this.accountMenuTemplate;
		const el = wrap.firstElementChild as HTMLElement | null;
		if (!el) {
			return null;
		}
		attachAccountMenu(el);
		return el;
	}

	private buildBrandmark(): HTMLElement {
		const mark = createEl('span', 'lb-redesign-brandmark');
		mark.setAttribute('aria-hidden', 'true');
		mark.innerHTML = '<svg class="lb-brand-badge" viewBox="0 0 120 120" focusable="false">'
			+ '<rect class="lb-badge-bg" width="120" height="120" rx="22" ry="22" fill="#17006B"/>'
			+ '<g class="lb-mark" transform="translate(17,8) scale(0.8167)">'
			+ '<rect class="lb-L-vert" x="15" y="17" width="22" height="91" rx="11" ry="11" fill="#FFFFFF"/>'
			+ '<rect class="lb-L-horiz" x="15" y="86" width="74" height="22" rx="11" ry="11" fill="#FFFFFF"/>'
			+ '<path class="lb-child-top" d="M 47 22 H 64 A 11 11 0 0 1 64 44 H 47 Z" fill="#BFADFF"/>'
			+ '<path class="lb-child-bot" d="M 47 54 H 70 A 11 11 0 0 1 70 76 H 47 Z" fill="#BF80FF"/>'
			+ '</g></svg>';
		return mark;
	}

	private buildCard(dashProject: IDashboardProject, variant: 'grid' | 'shelf' = 'grid'): HTMLElement {
		const project = dashProject.project;
		const card = createEl('article', `lb-dash-card lb-dash-card--${variant}`);
		card.tabIndex = 0;
		card.setAttribute('role', 'button');
		card.setAttribute('aria-label', `Open ${displayTitle(project)}`);
		setProjectSurfaceData(card, project);
		card.addEventListener('click', () => this.openProject(dashProject, card));
		card.addEventListener('keydown', event => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				this.openProject(dashProject, card);
			}
		});

		const map = createEl('div', 'lb-dash-card__map');
		if (dashProject.mapImageUrl) {
			map.style.backgroundImage = `url("${dashProject.mapImageUrl}")`;
			map.classList.add('has-image');
		}
		map.appendChild(createEl('span', 'lb-dash-card__place', projectPlaceLabel(project)));
		card.appendChild(map);

		const body = createEl('div', 'lb-dash-card__body');
		const titleRow = createEl('div', 'lb-dash-card__title-row');
		titleRow.appendChild(createEl('h2', 'lb-dash-card__title', displayTitle(project)));
		titleRow.appendChild(this.buildStatusBadge(dashProject.status));
		body.appendChild(titleRow);
		const stats = createEl('div', 'lb-dash-card__stats');
		stats.appendChild(createEl('strong', 'lb-dash-kpi', formatNumber(dashProject.quantity)));
		stats.appendChild(createEl('span', 'lb-dash-card__label', 'mailing addresses'));
		body.appendChild(stats);
		card.appendChild(body);

		const footer = createEl('div', 'lb-dash-card__footer');
		footer.appendChild(this.buildAvatar(project));
		footer.appendChild(createEl('span', 'lb-dash-muted', project.absoluteListUrl ? 'List ready' : 'Working selection'));
		if (project.absoluteListUrl) {
			footer.appendChild(createEl('span', 'lb-dash-print-tag', 'Downloadable'));
		}
		footer.appendChild(this.buildActionsButton(dashProject));
		card.appendChild(footer);
		return card;
	}

	private buildCreateModal(): HTMLElement {
		const overlay = createEl('div', 'lb-dash-create-overlay');
		const card = createEl('div', 'lb-dash-create-card');
		const header = createEl('div', 'lb-dash-create-card__header');
		header.appendChild(createEl('h2', '', 'New project'));
		const close = createEl('button', 'lb-dash-icon-btn');
		close.type = 'button';
		close.setAttribute('aria-label', 'Close');
		close.appendChild(createEl('span', 'material-icons', 'close'));
		close.addEventListener('click', () => overlay.remove());
		header.appendChild(close);
		card.appendChild(header);

		const body = createEl('div', 'lb-dash-create-card__body');
		body.appendChild(createEl('label', 'lb-dash-field-label', 'Project name'));
		const input = createEl('input', 'lb-dash-field-input');
		input.placeholder = 'e.g. Wilmington spring outreach';
		body.appendChild(input);
		body.appendChild(createEl(
			'p',
			'lb-dash-muted',
			'You can rename the project any time from the project workspace.',
		));
		card.appendChild(body);

		const footer = createEl('div', 'lb-dash-create-card__footer');
		const cancel = createEl('button', 'lb-dash-ghost-btn', 'Cancel');
		cancel.type = 'button';
		cancel.addEventListener('click', () => overlay.remove());
		const submit = createEl('button', 'lb-dash-create-btn', 'Create Project');
		submit.type = 'button';
		submit.addEventListener('click', evt => {
			const btn = evt.currentTarget as HTMLButtonElement | null;
			if (btn) {
				this.submitNewProjectForm(btn, input.value);
			}
		});
		input.addEventListener('keydown', evt => {
			if (evt.key === 'Enter') {
				evt.preventDefault();
				this.submitNewProjectForm(submit, input.value);
			}
		});
		footer.appendChild(cancel);
		footer.appendChild(submit);
		card.appendChild(footer);
		overlay.appendChild(card);
		overlay.addEventListener('click', event => {
			if (event.target === overlay) {
				overlay.remove();
			}
		});
		window.setTimeout(() => input.focus(), 20);
		return overlay;
	}

	private buildEmptyState(): HTMLElement {
		const empty = createEl('div', 'lb-dash-empty');
		empty.appendChild(this.buildBrandmark());
		empty.appendChild(createEl('h2', '', 'No projects match'));
		empty.appendChild(createEl('p', '', 'Try clearing the search or status filter, then your blox should reappear.'));
		return empty;
	}

	// Deal-forward PR A: top-level peer nav. "Projects" is the current
	// surface (active); "Deals" links to /deals/. Kept deliberately
	// minimal — two pills — so it reads as equal-weight peers rather
	// than a deep nav tree.
	// Deal-forward PR C: the peer nav (introduced in PR A as links to /
	// and /deals/) becomes the single global mode toggle (plan §6 Q4).
	// "Projects" → lists-mode, "Deals" → deals-mode; both stay on "/" and
	// re-lead the dashboard rather than navigating away. The preference
	// persists via /api/ui. The flat /deals/ list stays reachable through
	// the "All deals →" link in each mode's deal section.
	private buildPeerNav(): HTMLElement {
		const nav = createEl('nav', 'lb-dash-nav');
		nav.setAttribute('aria-label', 'Dashboard mode');
		const mk = (mode: DashboardMode, label: string): HTMLButtonElement => {
			const active = this.mode === mode;
			const btn = createEl(
				'button',
				active ? 'lb-dash-nav__item is-active' : 'lb-dash-nav__item',
				label,
			);
			btn.type = 'button';
			if (active) {
				btn.setAttribute('aria-current', 'page');
			}
			btn.addEventListener('click', () => this.setMode(mode));
			return btn;
		};
		nav.appendChild(mk('lists', 'Projects'));
		nav.appendChild(mk('deals', 'Deals'));
		return nav;
	}

	// Switch dashboard mode optimistically (re-render now), then persist
	// the preference best-effort. A failed persist leaves the in-session
	// mode applied; it just won't survive a reload.
	private setMode(mode: DashboardMode): void {
		if (this.mode === mode) {
			return;
		}
		this.mode = mode;
		this.render();
		svc.ui.setPreferredMode(mode).catch(err => {
			logger.warning('setMode: Failed to persist mode: %s', err);
		});
	}

	private buildHeader(): HTMLElement {
		const header = createEl('header', 'lb-dash-bar');
		const brand = createEl('div', 'lb-dash-bar__brand');
		brand.appendChild(this.buildBrandmark());
		const brandText = createEl('div', 'lb-dash-brand-text');
		brandText.appendChild(createEl('strong', '', 'Listblox'));
		brandText.appendChild(createEl('small', '', this.mode === 'deals' ? 'All deals' : 'All projects'));
		brand.appendChild(brandText);
		// Deal-forward PR A: peer-nav promoting Deals to a first-class
		// top-level destination alongside Projects. Nested inside the
		// brand cluster so the header's 3-column grid is unchanged.
		// "Projects" is the active surface; "Deals" routes to /deals/.
		brand.appendChild(this.buildPeerNav());
		header.appendChild(brand);

		const center = createEl('div', 'lb-dash-bar__center');
		const search = createEl('label', 'lb-dash-search');
		search.appendChild(createEl('span', 'material-icons', 'search'));
		const input = createEl('input', '');
		input.placeholder = 'Search projects...';
		input.value = this.query;
		// Save a reference so `documentKeyDown` (⌘K) can focus the
		// live input element, and so the re-render path below can
		// restore focus + caret position to the new input node.
		this.searchInputEl = input;
		input.addEventListener('input', () => {
			this.query = input.value;
			// `render()` removes and rebuilds the entire dashboard
			// `<section>`, which destroys this input — so the OS focus
			// was being kicked off the element after every keystroke.
			// Capture caret state pre-render, then restore on the
			// fresh input that `buildHeader()` will create.
			const caretStart = input.selectionStart;
			const caretEnd = input.selectionEnd;
			this.render();
			if (this.searchInputEl) {
				this.searchInputEl.focus();
				try {
					this.searchInputEl.setSelectionRange(caretStart, caretEnd);
				} catch (_) {
					// setSelectionRange throws on type="number" et al; this
					// input is type="text" so the try is just defensive.
				}
			}
		});
		search.appendChild(input);
		search.appendChild(createEl('span', 'lb-dash-shortcut', '⌘K'));
		center.appendChild(search);
		center.appendChild(this.buildViewSwitch());
		header.appendChild(center);

		const right = createEl('div', 'lb-dash-bar__right');
		const accountMenu = this.buildAccountMenu();
		if (accountMenu) {
			right.appendChild(accountMenu);
		}
		right.appendChild(this.buildCreateMenu());
		header.appendChild(right);
		return header;
	}

	// "New" split-menu (Phase 5C polish — discoverability slice). The
	// dashboard's primary create CTA used to be a single "New project"
	// button; it now opens an inline popover offering both "New List"
	// (the original project-create modal) and "New Deal" (standalone
	// Deal Calc — `/deals/new/`). Designed so additional create options
	// can be appended without restructuring the layout.
	private buildCreateMenu(): HTMLElement {
		const wrap = createEl('div', 'lb-dash-create-menu');
		const button = createEl('button', 'lb-dash-create-btn lb-dash-create-btn--has-menu');
		button.type = 'button';
		button.setAttribute('aria-haspopup', 'menu');
		button.setAttribute('aria-expanded', 'false');
		const label = createEl('span', 'lb-dash-create-btn__label', 'New');
		button.appendChild(label);
		const caret = createEl('span', 'lb-dash-create-btn__caret material-icons', 'expand_more');
		caret.setAttribute('aria-hidden', 'true');
		button.appendChild(caret);

		const popover = createEl('div', 'lb-dash-create-menu__popover');
		popover.setAttribute('role', 'menu');

		const newList = createEl('button', 'lb-dash-create-menu__item');
		newList.type = 'button';
		newList.setAttribute('role', 'menuitem');
		const newListIcon = createEl('span', 'material-icons lb-dash-create-menu__item-icon', 'add_road');
		newListIcon.setAttribute('aria-hidden', 'true');
		newList.appendChild(newListIcon);
		const newListBody = createEl('div', 'lb-dash-create-menu__item-body');
		newListBody.appendChild(createEl('strong', '', 'New List'));
		newListBody.appendChild(createEl('span', '', 'Create a project / mailing list from a boundary or filter.'));
		newList.appendChild(newListBody);
		newList.addEventListener('click', () => {
			close();
			document.body.appendChild(this.buildCreateModal());
		});

		const newDeal = createEl('a', 'lb-dash-create-menu__item');
		newDeal.href = '/deals/new/';
		newDeal.setAttribute('role', 'menuitem');
		const newDealIcon = createEl('span', 'material-icons lb-dash-create-menu__item-icon', 'calculate');
		newDealIcon.setAttribute('aria-hidden', 'true');
		newDeal.appendChild(newDealIcon);
		const newDealBody = createEl('div', 'lb-dash-create-menu__item-body');
		newDealBody.appendChild(createEl('strong', '', 'New Deal'));
		newDealBody.appendChild(createEl('span', '', 'Underwrite a flip or build — standalone, not tied to a project.'));
		newDeal.appendChild(newDealBody);

		// Deal-forward PR C: lead the create menu with the current mode's
		// primary action — "New Deal" first in deals-mode, "New List"
		// first in lists-mode.
		if (this.mode === 'deals') {
			newDeal.classList.add('lb-dash-create-menu__item--primary');
			popover.appendChild(newDeal);
			popover.appendChild(newList);
		} else {
			popover.appendChild(newList);
			popover.appendChild(newDeal);
		}

		wrap.appendChild(button);
		wrap.appendChild(popover);

		const positionPopover = () => {
			flipPopoverHorizontally(popover, 'lb-dash-create-menu__popover--align-left');
		};
		const close = () => {
			wrap.classList.remove('lb-dash-create-menu--open');
			button.setAttribute('aria-expanded', 'false');
			document.removeEventListener('click', onDocClick, true);
			document.removeEventListener('keydown', onKey, true);
			window.removeEventListener('resize', positionPopover);
		};
		const open = () => {
			wrap.classList.add('lb-dash-create-menu--open');
			button.setAttribute('aria-expanded', 'true');
			window.requestAnimationFrame(positionPopover);
			document.addEventListener('click', onDocClick, true);
			document.addEventListener('keydown', onKey, true);
			window.addEventListener('resize', positionPopover);
		};
		const onDocClick = (evt: Event) => {
			if (!(evt.target instanceof Node)) {
				return;
			}
			if (!wrap.contains(evt.target)) {
				close();
			}
		};
		const onKey = (evt: KeyboardEvent) => {
			if (evt.key === 'Escape') {
				close();
				button.focus();
			}
		};
		button.addEventListener('click', evt => {
			evt.stopPropagation();
			if (wrap.classList.contains('lb-dash-create-menu--open')) {
				close();
			} else {
				open();
			}
		});
		return wrap;
	}

	private buildListRow(dashProject: IDashboardProject, isHeader: boolean = false): HTMLElement {
		if (isHeader) {
			const header = createEl('div', 'lb-dash-list-row lb-dash-list-row--header');
			['', 'Project', 'Addresses', 'Status', 'State', ''].forEach(label => {
				header.appendChild(createEl('span', '', label));
			});
			return header;
		}
		const project = dashProject.project;
		const row = createEl('article', 'lb-dash-list-row');
		row.tabIndex = 0;
		row.setAttribute('role', 'button');
		setProjectSurfaceData(row, project);
		row.addEventListener('click', () => this.openProject(dashProject, row));
		row.addEventListener('keydown', event => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				this.openProject(dashProject, row);
			}
		});
		const thumb = createEl('div', 'lb-dash-list-row__thumb');
		if (dashProject.mapImageUrl) {
			thumb.style.backgroundImage = `url("${dashProject.mapImageUrl}")`;
			thumb.classList.add('has-image');
		}
		row.appendChild(thumb);
		const name = createEl('div', '');
		name.appendChild(createEl('strong', '', displayTitle(project)));
		name.appendChild(createEl('small', '', projectPlaceLabel(project)));
		row.appendChild(name);
		row.appendChild(createEl('span', 'lb-dash-kpi', formatNumber(dashProject.quantity)));
		row.appendChild(this.buildStatusBadge(dashProject.status));
		row.appendChild(createEl('span', 'lb-dash-muted', project.absoluteListUrl ? 'Ready' : 'Draft'));
		row.appendChild(this.buildActionsButton(dashProject));
		return row;
	}

	private buildMapPin(dashProject: IDashboardProject, index: number): HTMLElement {
		const pin = createEl('article', 'lb-dash-map-pin');
		const project = dashProject.project;
		setProjectSurfaceData(pin, project);
		pin.tabIndex = 0;
		pin.setAttribute('role', 'button');
		pin.addEventListener('click', () => this.openProject(dashProject, pin));
		pin.addEventListener('keydown', event => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				this.openProject(dashProject, pin);
			}
		});
		pin.dataset.slug = project.slug;
		pin.dataset.index = String(index);
		const cube = createEl('span', 'lb-dash-map-pin__cube');
		cube.appendChild(createEl('span', 'face'));
		cube.appendChild(createEl('span', 'face face--top'));
		cube.appendChild(createEl('span', 'face face--side'));
		pin.appendChild(cube);
		const text = createEl('span', 'lb-dash-map-pin__text');
		text.appendChild(createEl('strong', '', displayTitle(project)));
		text.appendChild(createEl('small', '', `${formatNumber(dashProject.quantity)} addresses`));
		pin.appendChild(text);
		pin.appendChild(this.buildActionsButton(dashProject));
		return pin;
	}

	private buildOnboarding(): HTMLElement {
		const card = createEl('section', 'lb-dash-onboarding');
		const body = createEl('div', 'lb-dash-onboarding__body');
		body.appendChild(createEl('h2', '', 'Build projects by stacking map filters'));
		body.appendChild(createEl('p', '', 'Each project is a saved blox: a place, owner, or drawn area you can refine, price, print, and revisit.'));
		card.appendChild(body);
		const steps = createEl('div', 'lb-dash-onboarding__steps');
		['Pick a place', 'Layer filters', 'Send to print'].forEach((label, idx) => {
			const step = createEl('span', idx === 0 ? 'is-done' : '', `${idx + 1}. ${label}`);
			steps.appendChild(step);
		});
		card.appendChild(steps);
		const create = createEl('button', 'lb-dash-create-btn', 'Start a project');
		create.type = 'button';
		create.addEventListener('click', () => document.body.appendChild(this.buildCreateModal()));
		card.appendChild(create);
		// Dedicated class so SCSS can anchor the dismiss to the card's
		// top-right corner instead of letting it flow as the last child
		// (which on narrow viewports stacked it at the bottom-left when
		// the card flipped to flex-direction: column).
		const dismiss = createEl('button', 'lb-dash-icon-btn lb-dash-onboarding__dismiss');
		dismiss.type = 'button';
		dismiss.title = 'Dismiss';
		dismiss.setAttribute('aria-label', 'Dismiss');
		dismiss.appendChild(createEl('span', 'material-icons', 'close'));
		dismiss.addEventListener('click', () => {
			this.onboardingVisible = false;
			window.localStorage.setItem(ONBOARDING_STORAGE_KEY, '1');
			this.render();
		});
		card.appendChild(dismiss);
		return card;
	}

	private buildStatusBadge(status: DashboardStatus): HTMLElement {
		const badge = createEl('span', 'lb-dash-status', STATUS_LABELS[status]);
		const dot = createEl('i');
		dot.style.background = STATUS_DOTS[status];
		badge.prepend(dot);
		return badge;
	}

	// ─────────── Deal-forward PR A: "Recent deals" shelf ───────────

	// Cap the shelf so it stays a quick-access strip, not a full list.
	// "All deals →" routes to the dedicated /deals/ surface for the
	// complete set.
	private static readonly DEALS_SHELF_MAX = 8;

	// Returns the shelf section, or null when the user has no deals
	// (the shelf is purely additive — no empty state on the dashboard;
	// the "New Deal" create-menu option already covers discovery of
	// the deal flow for first-time users).
	private buildDealsShelf(): HTMLElement | null {
		if (this.deals.length < 1) {
			return null;
		}
		const section = createEl('section', 'lb-dash-deals');
		const header = createEl('div', 'lb-dash-deals__header');
		const heading = createEl('div', 'lb-dash-deals__heading');
		heading.appendChild(createEl('h2', 'lb-dash-deals__title', 'Recent deals'));
		heading.appendChild(createEl(
			'span',
			'lb-dash-deals__sub',
			'Underwriting scenarios across all your projects.',
		));
		header.appendChild(heading);
		const all = createEl('a', 'lb-dash-deals__all');
		all.href = '/deals/';
		all.appendChild(createEl('span', '', 'All deals'));
		all.appendChild(createEl('span', 'material-icons', 'arrow_forward'));
		header.appendChild(all);
		section.appendChild(header);

		const board = createEl('div', 'lb-dash-deals__board');
		this.deals
			.slice(0, ProjectDashboardView.DEALS_SHELF_MAX)
			.forEach(deal => board.appendChild(this.buildDealCard(deal)));
		section.appendChild(board);
		return section;
	}

	// Deal-forward PR B: the per-deal card is now the shared DealBlock
	// component (`variant: 'card'`). The shelf still owns the surrounding
	// board + header layout above.
	private buildDealCard(deal: IDealSummary): HTMLElement {
		return renderDealBlock(dealBlockFromSummary(deal), {variant: 'card'});
	}

	// ─────────── Deal-forward PR C: deals-mode layout ───────────

	// The deals-mode lead — a full (uncapped) grid of the user's deals
	// rendered with the shared DealBlock card. Reuses the shelf's header +
	// board chrome; this is the primary surface in deals-mode, so it shows
	// every deal rather than the capped PR A shelf. Empty → a deal-create
	// nudge.
	private buildDealsLead(): HTMLElement {
		const section = createEl('section', 'lb-dash-deals lb-dash-deals--lead');
		const header = createEl('div', 'lb-dash-deals__header');
		const heading = createEl('div', 'lb-dash-deals__heading');
		heading.appendChild(createEl('h2', 'lb-dash-deals__title', 'Your deals'));
		heading.appendChild(createEl(
			'span',
			'lb-dash-deals__sub',
			'Underwriting scenarios across all your projects.',
		));
		header.appendChild(heading);
		const all = createEl('a', 'lb-dash-deals__all');
		all.href = '/deals/';
		all.appendChild(createEl('span', '', 'All deals'));
		all.appendChild(createEl('span', 'material-icons', 'arrow_forward'));
		header.appendChild(all);
		section.appendChild(header);

		if (this.deals.length < 1) {
			const empty = createEl('div', 'lb-dash-deals__empty');
			empty.appendChild(createEl('span', 'material-icons', 'calculate'));
			empty.appendChild(createEl('strong', '', 'No deals yet'));
			empty.appendChild(createEl('span', 'lb-dash-muted', 'Underwrite a flip or build to see it here.'));
			const cta = createEl('a', 'lb-dash-create-btn lb-dash-deals__empty-cta', 'New deal');
			cta.href = '/deals/new/';
			empty.appendChild(cta);
			section.appendChild(empty);
			return section;
		}

		const board = createEl('div', 'lb-dash-deals__board');
		this.deals.forEach(deal => board.appendChild(this.buildDealCard(deal)));
		section.appendChild(board);
		return section;
	}

	// In deals-mode, projects demote to a secondary section below the deal
	// lead. Compact heading + the existing project grid, respecting the
	// current search filter. Reuses `renderGrid` / `buildEmptyState`.
	private buildProjectsDemoted(projects: Array<IDashboardProject>): HTMLElement {
		const section = createEl('section', 'lb-dash-projects-demoted');
		const header = createEl('div', 'lb-dash-deals__header');
		const heading = createEl('div', 'lb-dash-deals__heading');
		heading.appendChild(createEl('h2', 'lb-dash-deals__title', 'Your projects'));
		heading.appendChild(createEl('span', 'lb-dash-deals__sub', 'Saved mailing-list projects.'));
		header.appendChild(heading);
		section.appendChild(header);
		if (projects.length < 1) {
			section.appendChild(this.buildEmptyState());
			return section;
		}
		section.appendChild(this.renderGrid(projects));
		return section;
	}

	private buildSubbar(projects: Array<IDashboardProject>): HTMLElement {
		const subbar = createEl('div', 'lb-dash-subbar');
		const total = projects.reduce((sum, project) => sum + (project.quantity || 0), 0);
		subbar.appendChild(createEl(
			'span',
			'lb-dash-subbar__count',
			`${projects.length} ${projects.length === 1 ? 'project' : 'projects'} · ${total.toLocaleString()} addresses`,
		));
		const spacer = createEl('span', 'lb-dash-spacer');
		subbar.appendChild(spacer);
		(['all', 'draft', 'checkout', 'ready'] as Array<DashboardStatus | 'all'>).forEach(status => {
			const chip = createEl('button', this.filterStatus === status ? 'lb-dash-chip is-on' : 'lb-dash-chip');
			chip.type = 'button';
			chip.textContent = status === 'all' ? 'All' : STATUS_LABELS[status];
			chip.addEventListener('click', () => {
				this.filterStatus = status;
				this.render();
			});
			subbar.appendChild(chip);
		});
		const sort = createEl('select', 'lb-dash-sort');
		const opts: Array<[DashboardSortMode, string]> = [
			['recent', 'Sort: Recently updated'],
			['name', 'Sort: Name'],
			['size', 'Sort: Largest'],
			['status', 'Sort: Status'],
		];
		opts.forEach(([value, label]) => {
			const opt = createEl('option');
			opt.value = value;
			opt.textContent = label;
			opt.selected = this.sortMode === value;
			sort.appendChild(opt);
		});
		sort.addEventListener('change', () => {
			this.sortMode = <DashboardSortMode>sort.value;
			this.render();
		});
		subbar.appendChild(sort);
		return subbar;
	}

	private buildViewSwitch(): HTMLElement {
		const seg = createEl('div', 'lb-dash-view-seg');
		const views: Array<[DashboardViewMode, string, string]> = [
			['grid', 'grid_view', 'Grid'],
			['shelf', 'view_carousel', 'Shelf'],
			['map', 'location_on', 'Map'],
			['list', 'view_list', 'List'],
		];
		views.forEach(([mode, icon, label]) => {
			const btn = createEl('button', this.viewMode === mode ? 'is-active' : '');
			btn.type = 'button';
			btn.appendChild(createEl('span', 'material-icons', icon));
			btn.appendChild(createEl('span', '', label));
			btn.addEventListener('click', () => {
				this.viewMode = mode;
				this.render();
			});
			seg.appendChild(btn);
		});
		return seg;
	}

	private closeActionMenu(): void {
		if (this.actionMenuEl) {
			this.actionMenuEl.remove();
			this.actionMenuEl = null;
		}
	}

	private destroyMap(): void {
		if (this.map) {
			this.map.remove();
		}
		this.map = null;
		this.mapCanvasEl = null;
		this.mapPinsEl = null;
	}

	private documentClick(event: MouseEvent): void {
		if (this.actionMenuEl && !this.actionMenuEl.contains(<Node>event.target)) {
			this.closeActionMenu();
		}
	}

	private downloadProject(dashProject: IDashboardProject, ownerNames: boolean = false): void {
		if (!dashProject.project.absoluteListUrl) {
			return;
		}
		window.location.assign(ownerNames ?
			`${dashProject.project.absoluteListUrl}?opt=owner` :
			dashProject.project.absoluteListUrl);
	}

	private async duplicateProject(dashProject: IDashboardProject): Promise<void> {
		const clone = await svc.project.clone(dashProject.project.slug);
		window.location.assign(clone.absoluteUrl);
	}

	private filteredProjects(): Array<IDashboardProject> {
		const q = this.query.trim().toLocaleLowerCase();
		let projects = this.projects.slice();
		if (q) {
			projects = projects.filter(obj => {
				const project = obj.project;
				return displayTitle(project).toLocaleLowerCase().includes(q)
					|| project.slug.toLocaleLowerCase().includes(q);
			});
		}
		if (this.filterStatus !== 'all') {
			projects = projects.filter(obj => obj.status === this.filterStatus);
		}
		switch (this.sortMode) {
			case 'name':
				projects.sort((a, b) => projectSortName(a.project).localeCompare(projectSortName(b.project)));
				break;
			case 'size':
				projects.sort((a, b) => (b.quantity || 0) - (a.quantity || 0));
				break;
			case 'status':
				projects.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
				break;
			case 'recent':
			default:
				projects.sort((a, b) => b.project.id - a.project.id);
				break;
		}
		return projects;
	}

	private async init(): Promise<void> {
		this.renderLoading();
		// Deal-forward PR C: hydrate the dashboard mode in parallel with
		// projects. UI prefs are best-effort — a slow/failed fetch falls
		// back to the default 'lists' layout rather than blocking.
		const uiP = svc.ui.get().catch(err => {
			logger.warning('init: Failed to load UI prefs: %s', err);
			return null;
		});
		try {
			const [ui, projects] = await Promise.all([uiP, svc.project.visibleList()]);
			if (ui && ui.preferredMode) {
				this.mode = ui.preferredMode;
			}
			this.projects = projects.map(project => ({
				latitude: null,
				longitude: null,
				mapImageUrl: '',
				project,
				quantity: null,
				status: statusForProject(project),
				zoom: null,
			}));
			if (this.mode === 'deals') {
				// Deals are the lead in deals-mode — load them before the
				// first content render so the lead isn't briefly empty.
				try {
					this.deals = await svc.deal.list();
				} catch (err) {
					logger.warning('init: Failed to load deals: %s', err);
				}
				this.render();
			} else {
				this.render();
				// Deal-forward PR A: in lists-mode the "Recent deals" shelf
				// is secondary, so fetch it after the project render and
				// fold it in on success. Never blocks the projects body.
				void this.loadDeals();
			}
			this.playFlyBackIfRequested();
			this.syncProjectMetadata();
		} catch (err) {
			logger.error('init: Failed to load projects: %s', err);
			this.renderError();
		}
	}

	private async loadDeals(): Promise<void> {
		try {
			this.deals = await svc.deal.list();
			if (this.deals.length > 0) {
				this.render();
			}
		} catch (err) {
			// Non-fatal: the projects dashboard already rendered. Just
			// skip the shelf.
			logger.warning('loadDeals: Failed to load deals: %s', err);
		}
	}

	private initMap(projects: Array<IDashboardProject>): void {
		if (this.viewMode !== 'map' || !this.mapCanvasEl || !this.mapPinsEl) {
			this.destroyMap();
			return;
		}
		if (!MAPBOX_ACCESS_TOKEN) {
			this.positionMapPins(projects);
			return;
		}
		if (!this.map) {
			mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
			this.map = new mapboxgl.Map({
				attributionControl: false,
				center: [-95.5, 38.5],
				container: this.mapCanvasEl,
				interactive: true,
				style: DESIGN_MAP_STYLE_URL || DEFAULT_MAP_STYLE_URL,
				zoom: 3.3,
			});
			const recompute = () => this.positionMapPins(this.filteredProjects());
			this.map.on('load', recompute);
			this.map.on('move', recompute);
			this.map.on('zoom', recompute);
			this.map.on('resize', recompute);
		}
		this.positionMapPins(projects);
	}

	private openActionMenu(dashProject: IDashboardProject, anchor: HTMLElement): void {
		this.closeActionMenu();
		const menu = createEl('div', 'lb-dash-action-menu');
		const addButton = (label: string, action: () => void | Promise<void>, disabled: boolean = false): void => {
			const button = createEl('button', '', label);
			button.type = 'button';
			button.disabled = disabled;
			button.addEventListener('click', event => {
				event.stopPropagation();
				this.closeActionMenu();
				action();
			});
			menu.appendChild(button);
		};
		addButton('Open project', () => this.openProject(dashProject, anchor.closest('[data-project-id]') as HTMLElement || anchor));
		addButton('Download list', () => this.downloadProject(dashProject), !dashProject.project.absoluteListUrl);
		addButton('Download with owner names', () => this.downloadProject(dashProject, true), !dashProject.project.absoluteListUrl || !dashProject.project.downloadWithOwnerNameEnabled);
		addButton('Duplicate', () => this.duplicateProject(dashProject));
		addButton('Archive', () => this.archiveProject(dashProject));
		const rect = anchor.getBoundingClientRect();
		menu.style.top = `${rect.bottom + 8}px`;
		menu.style.left = `${Math.max(12, rect.right - 220)}px`;
		document.body.appendChild(menu);
		// After layout, nudge the menu back inside the viewport if its
		// computed bounds spill off the right or bottom edge. The first-
		// pass positioning above is a guess — only after the menu's
		// real dimensions are measurable can we correct.
		window.requestAnimationFrame(() => clampFixedMenuToViewport(menu, {
			bottom: rect.bottom,
			left: rect.left,
			right: rect.right,
			top: rect.top,
		}));
		this.actionMenuEl = menu;
	}

	private async openProject(dashProject: IDashboardProject, origin: HTMLElement): Promise<void> {
		if (this.navPending) {
			return;
		}
		this.navPending = true;
		origin.classList.add('is-flying-origin');
		try {
			await animateProjectFly({
				contentEl: origin,
				from: rectFromElement(origin),
				to: {
					height: window.innerHeight,
					left: 0,
					top: 0,
					width: window.innerWidth,
				},
			});
		} catch (err) {
			logger.warning('openProject: Fly animation failed: %s', err);
		}
		window.location.assign(dashProject.project.absoluteUrl);
	}

	private positionMapPins(projects: Array<IDashboardProject>): void {
		if (!this.mapPinsEl) {
			return;
		}
		for (const pin of Array.from(this.mapPinsEl.querySelectorAll<HTMLElement>('.lb-dash-map-pin'))) {
			const index = Number(pin.dataset.index || 0);
			const dashProject = projects[index];
			if (!dashProject) {
				continue;
			}
			if (this.map && dashProject.longitude !== null && dashProject.latitude !== null) {
				const point = this.map.project([dashProject.longitude, dashProject.latitude]);
				pin.style.left = `${point.x}px`;
				pin.style.top = `${point.y}px`;
			} else {
				const fallback = syntheticPointForProject(dashProject.project);
				pin.style.left = `${fallback.left}%`;
				pin.style.top = `${fallback.top}%`;
			}
		}
	}

	private playFlyBackIfRequested(): void {
		if (this.maybePlayedFlyBack) {
			return;
		}
		this.maybePlayedFlyBack = true;
		const payload = consumeProjectFlyBack();
		if (!payload) {
			return;
		}
		window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
			const target = document.querySelector(projectSurfaceSelector(payload.projectId));
			if (!(target instanceof HTMLElement)) {
				return;
			}
			animateProjectFly({
				activeOverlay: true,
				contentEl: target,
				from: {
					height: window.innerHeight,
					left: 0,
					top: 0,
					width: window.innerWidth,
				},
				to: rectFromElement(target),
			});
		}));
	}

	private queueRender(): void {
		if (this.renderQueued !== null) {
			window.clearTimeout(this.renderQueued);
		}
		this.renderQueued = window.setTimeout(() => {
			this.renderQueued = null;
			this.render();
		}, 60);
	}

	private render(): void {
		this.destroyMap();
		this.closeActionMenu();
		if (this.dashEl) {
			this.dashEl.remove();
		}
		const projects = this.filteredProjects();
		const dash = createEl('section', this.bodyClasses());
		this.dashEl = dash;
		dash.appendChild(this.buildHeader());
		if (this.mode === 'deals') {
			// Deal-forward PR C: deals-mode leads with the full deal grid;
			// projects demote to a section below. The project view switcher
			// (grid/shelf/map/list) governs the lists-mode body only, so
			// the deal-led layout ignores it.
			dash.appendChild(this.buildDealsLead());
			dash.appendChild(this.buildProjectsDemoted(projects));
			this.root.appendChild(dash);
			return;
		}
		if (this.viewMode !== 'map') {
			// Deal-forward PR A: the "Recent deals" shelf sits above the
			// projects body as a peer surface — deals are first-class,
			// not buried under projects. Hidden in map mode (the map
			// fills the viewport) and when the user has no deals yet.
			const dealsShelf = this.buildDealsShelf();
			if (dealsShelf) {
				dash.appendChild(dealsShelf);
			}
			dash.appendChild(this.buildSubbar(projects));
			if (this.onboardingVisible) {
				dash.appendChild(this.buildOnboarding());
			}
		}
		dash.appendChild(this.renderBody(projects));
		this.root.appendChild(dash);
		if (this.viewMode === 'map') {
			this.initMap(projects);
		}
	}

	private renderBody(projects: Array<IDashboardProject>): HTMLElement {
		if (projects.length < 1) {
			return this.buildEmptyState();
		}
		switch (this.viewMode) {
			case 'shelf':
				return this.renderShelf(projects);
			case 'map':
				return this.renderMap(projects);
			case 'list':
				return this.renderList(projects);
			case 'grid':
			default:
				return this.renderGrid(projects);
		}
	}

	private renderError(): void {
		if (this.dashEl) {
			this.dashEl.remove();
		}
		const dash = createEl('section', this.bodyClasses());
		dash.appendChild(this.buildHeader());
		const err = createEl('div', 'lb-dash-empty');
		err.appendChild(this.buildBrandmark());
		err.appendChild(createEl('h2', '', 'Projects could not load'));
		err.appendChild(createEl('p', '', 'Refresh the page and try again.'));
		dash.appendChild(err);
		this.root.appendChild(dash);
		this.dashEl = dash;
	}

	private renderGrid(projects: Array<IDashboardProject>): HTMLElement {
		const grid = createEl('div', 'lb-dash-grid');
		projects.forEach(project => grid.appendChild(this.buildCard(project)));
		return grid;
	}

	private renderList(projects: Array<IDashboardProject>): HTMLElement {
		const list = createEl('div', 'lb-dash-list');
		list.appendChild(this.buildListRow(projects[0], true));
		projects.forEach(project => list.appendChild(this.buildListRow(project)));
		return list;
	}

	private renderLoading(): void {
		const dash = createEl('section', this.bodyClasses());
		dash.appendChild(this.buildHeader());
		const loading = createEl('div', 'lb-dash-empty');
		loading.appendChild(this.buildBrandmark());
		loading.appendChild(createEl('h2', '', 'Loading projects'));
		loading.appendChild(createEl('p', '', 'Fetching your saved blox...'));
		dash.appendChild(loading);
		this.root.appendChild(dash);
		this.dashEl = dash;
	}

	private renderMap(projects: Array<IDashboardProject>): HTMLElement {
		const shell = createEl('div', 'lb-dash-map-shell');
		const canvas = createEl('div', 'lb-dash-map-canvas');
		const fallback = createEl('div', 'lb-dash-map-fallback');
		canvas.appendChild(fallback);
		shell.appendChild(canvas);
		const overlay = createEl('div', 'lb-dash-map-overlay');
		overlay.appendChild(createEl('strong', '', `${projects.length} projects on the map`));
		overlay.appendChild(createEl('span', '', 'Project pins use saved map centers when available.'));
		shell.appendChild(overlay);
		const pins = createEl('div', 'lb-dash-map-pins');
		projects.forEach((project, index) => pins.appendChild(this.buildMapPin(project, index)));
		shell.appendChild(pins);
		this.mapCanvasEl = canvas;
		this.mapPinsEl = pins;
		return shell;
	}

	private renderShelf(projects: Array<IDashboardProject>): HTMLElement {
		const shelf = createEl('div', 'lb-dash-shelf');
		(['draft', 'checkout', 'ready'] as Array<DashboardStatus>).forEach(status => {
			const groupProjects = projects.filter(project => project.status === status);
			if (groupProjects.length < 1) {
				return;
			}
			const group = createEl('section', 'lb-dash-shelf__group');
			const header = createEl('div', 'lb-dash-shelf__header');
			header.appendChild(this.buildStatusBadge(status));
			header.appendChild(createEl('span', 'lb-dash-muted', `${groupProjects.length} ${groupProjects.length === 1 ? 'project' : 'projects'}`));
			group.appendChild(header);
			const board = createEl('div', 'lb-dash-shelf__board');
			groupProjects.forEach(project => board.appendChild(this.buildCard(project, 'shelf')));
			group.appendChild(board);
			shelf.appendChild(group);
		});
		return shelf;
	}

	// POST to projects:list (the root URL) to create a fresh project.
	// listblox/projects/views.py:ProjectListView.post creates a new
	// Project for the current user and redirects 302 -> /<new_slug>/.
	// fetch follows 3xx by default; response.url ends up at the new
	// detail page, and window.location.href takes us there. Same
	// effective behavior as the legacy hidden-form submit but without
	// the DOM-form dependency PR #20 removed from base.html. Note: the
	// modal's "Project name" input is currently ignored by the backend
	// — Project.create(user_pk) doesn't accept a name. Tracked in
	// OPEN_TASKS as a separate contract fix.
	private async submitNewProjectForm(submitBtn: HTMLButtonElement, title?: string): Promise<void> {
		const originalLabel = submitBtn.textContent;
		submitBtn.disabled = true;
		submitBtn.textContent = 'Creating…';
		const main = document.querySelector<HTMLElement>('[data-projects-list-url]');
		const url = main?.dataset.projectsListUrl || '/';
		const body = new URLSearchParams();
		const trimmedTitle = (title || '').trim();
		if (trimmedTitle) {
			body.set('title', trimmedTitle);
		}
		try {
			const response = await fetch(url, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Accept': 'text/html',
					'Content-Type': 'application/x-www-form-urlencoded',
					'X-CSRFToken': extractCookie(CSRF_COOKIE_NAME) || '',
					'X-Requested-With': 'XMLHttpRequest',
				},
				body,
			});
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}
			window.location.href = response.url;
		} catch (err) {
			logger.error('submitNewProjectForm:', err);
			submitBtn.disabled = false;
			submitBtn.textContent = originalLabel;
		}
	}

	private async syncProjectMetadata(): Promise<void> {
		await Promise.all(this.projects.map(async dashProject => {
			try {
				if (dashProject.project.invoiceId !== null) {
					const invoice = await svc.invoice.get(dashProject.project.invoiceId);
					dashProject.quantity = invoice.totalQuantity;
				}
			} catch (err) {
				logger.warning('syncProjectMetadata: Failed invoice sync for %s: %s', dashProject.project.slug, err);
			}
			try {
				const cfgs = await svc.map.list({
					image: 'sync',
					projectSlug: dashProject.project.slug,
				});
				if (cfgs.length > 0) {
					const cfg = cfgs[0];
					dashProject.latitude = cfg.latitude;
					dashProject.longitude = cfg.longitude;
					dashProject.mapImageUrl = cfg.imageUrl;
					dashProject.zoom = cfg.zoom;
				}
			} catch (err) {
				logger.warning('syncProjectMetadata: Failed map-image sync for %s: %s', dashProject.project.slug, err);
			}
			this.queueRender();
		}));
	}
}
